import { compact } from '@leland-dev/leland-ui-library';
import { isEmpty, isNil, omitBy } from 'lodash-es';
import { type ReadonlyURLSearchParams } from 'next/dist/client/components/navigation';
import type { UrlObject } from 'url';

import { type ReferralType } from '../__generated-gql-types__/globalTypes';

import { APPLICANT_SITE_URL } from './constants';
import { REFERRAL_TYPE_MAP } from './referrals';
import { LELAND_PLUS_REFERRING_COACH_KEY, LelandLocalStorage } from './storage';
// we don't want to pass around routers.
// but we want to get the login path with a redirect back and carry any existing search params.
export const getLoginRedirectString = (
  path: string | null,
  searchParams = {},
) => {
  const newParams = {
    ...searchParams,
    returnTo: getUrlString(path, searchParams, APPLICANT_SITE_URL),
  };
  const url = getUrlString('/login', newParams, APPLICANT_SITE_URL);
  return url;
};

export const getSignupRedirectString = (
  redirectUrl?: string,
  queryParams?: Record<string, string>,
): string => {
  return getUrlString(`/signup`, {
    returnTo: redirectUrl ?? '',
    ...queryParams,
  });
};

// weird typeform use case that builds none spec url
export const getHashUrlString = (
  url: string,
  query: UrlObject['query'],
): string => {
  if (isEmpty(query)) {
    return url;
  }

  /* eslint-disable @typescript-eslint/no-unsafe-argument */
  const paramsString = Object.entries(omitBy(query, isNil))
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value
          .filter(compact)
          .map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
          .join('&');
      } else {
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      }
    })
    .join('&');
  /* eslint-enable @typescript-eslint/no-unsafe-argument */

  const char = '#';
  return `${url}${url.includes(char) ? '&' : char}${paramsString}`;
};

// this should be the default case for almost all next navigation. We want strings to work with the new router API not objects.
export const getUrlString = (
  url: string | null | undefined = '',
  query?: Record<string, unknown> | ReadonlyURLSearchParams | null,
  rootUrl = APPLICANT_SITE_URL,
): string => {
  const properUrl = new URL(`${rootUrl}${url}`);

  if (query) {
    for (const key in query) {
      if (query[key]) {
        if (Array.isArray(query[key])) {
          query[key].forEach((value) => {
            properUrl.searchParams.append(key, value as string);
          });
        } else {
          properUrl.searchParams.set(key, query[key] as string);
        }
      }
    }
  }

  return properUrl.href;
};

// working with url search params objects everywhere is tedious. Let's just convert them to an object, and then we'll convert them back in the getUrlString function before use.
export const convertUrlSearchParamsToObject = (
  searchParams: ReadonlyURLSearchParams | URLSearchParams | null,
): Record<string, string | string[]> => {
  const obj: Record<string, string | string[]> = {};
  searchParams?.forEach((value, key) => {
    // if key already exists we need to figure out if it's already an array or not
    // and then append or convert to arry depending
    if (obj[key]) {
      if (Array.isArray(obj[key])) {
        obj[key].push(value);
      } else {
        obj[key] = [obj[key], value] as string[];
      }
    } else if (value) {
      obj[key] = value;
    }
  });
  return obj;
};

// take an object of any type and safely convert it to a URLSearchParams object.
export const convertObjectToSearchParams = (
  searchParams: Record<string, unknown>,
): URLSearchParams => {
  const urlSearchParams = new URLSearchParams();
  for (const key in searchParams) {
    // we can't hand it undefined/nulls or it'll convert those to strings by default and break things.
    if (searchParams[key]) {
      urlSearchParams.set(key, searchParams[key] as string);
    }
  }
  return urlSearchParams;
};

//payment link comes in as a search param.
export const getStripePaymentLinkObject = (
  searchParams: ReadonlyURLSearchParams | URLSearchParams | null,
  email: string,
): string | undefined => {
  if (searchParams?.get('payment_link')) {
    if (searchParams?.get('coach')) {
      LelandLocalStorage.setItem(
        LELAND_PLUS_REFERRING_COACH_KEY,
        searchParams?.get('coach'),
      );
    }
    return getUrlString(searchParams?.get('payment_link') || '', {
      prefilled_email: email,
    });
  }
};

export const getCustomerReferralUrl = (
  referralCode: string,
  referralType: ReferralType,
): string => {
  return `${APPLICANT_SITE_URL}/signup?referralCode=${referralCode}&referralType=${REFERRAL_TYPE_MAP[referralType]}`;
};

// Helper function to parse fragment parameters
export const parseFragmentParams = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};

  const fragment = window.location.hash.substring(1); // Remove the '#'
  if (!fragment) return {};

  const params: Record<string, string> = {};
  const pairs = fragment.split('&');

  for (const pair of pairs) {
    const [key, value] = pair.split('=');
    if (key && value) {
      params[decodeURIComponent(key)] = decodeURIComponent(value);
    }
  }

  return params;
};

export const extractDomain = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;
    const parts = hostname.split('.');
    if (parts.length <= 2) return hostname; // already bare (e.g. localhost)
    return parts.slice(-2).join('.');
  } catch {
    // Fallback if URL parsing fails
    // Remove https:// or http://, and then split by / and take the last two parts of the domain.
    const host = url
      .replace(/^https?:\/\//, '') // drop scheme
      .replace(/^\.*/, '') // drop leading dots
      .split('/')[0] // keep host[:port]
      .split(':')[0]; // drop port

    const parts = host.split('.');
    if (parts.length <= 2) return host; // already bare (e.g. localhost)
    return parts.slice(-2).join('.');
  }
};

export const stateToQueryParams = (
  state: Record<string, unknown>,
): Record<string, string | string[]> => {
  /**
   * Converts a state object into a Record<string, string | string[]> for use as query params.
   * - If the value is a string, number, or boolean, it is stringified.
   * - If the value is an array of primitives, it is converted to string[].
   * - If the value is an object (not an array), it is JSON.stringified.
   * - Null and undefined values are omitted.
   */
  const result: Record<string, string | string[]> = {};

  for (const [key, value] of Object.entries(state)) {
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      result[key] = String(value);
    } else if (
      Array.isArray(value) &&
      value.every(
        (v) =>
          typeof v === 'string' ||
          typeof v === 'number' ||
          typeof v === 'boolean' ||
          v === null ||
          v === undefined,
      )
    ) {
      // Array of primitives: convert to string[]
      result[key] = value
        .filter((v) => v !== null && v !== undefined)
        .map((v) => String(v));
    } else if (value) {
      // Object: JSON.stringify
      result[key] = JSON.stringify(value);
    }
    // null, undefined, or other types are omitted
  }

  return result;
};

export const queryParamsToState = <T extends Record<string, unknown>>(
  state: Record<string, string | string[] | undefined>,
): T => {
  /**
   * Decodes a flat object with string or string[] values (from query params)
   * into a nested object of type T, reversing the logic of stateToQueryParams.
   * - If the value is a valid JSON string, it is parsed as an object.
   * - If the value is a string 'true'/'false', it is converted to boolean.
   * - If the value is a string representing a number, it is converted to number.
   * - If the value is an array, each element is converted as above.
   * - Otherwise, the value is left as a string.
   */
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(state)) {
    if (typeof value === 'string') {
      // Try to parse as JSON object
      try {
        const parsed = JSON.parse(value) as unknown;
        // Only use parsed if it's an object or array
        if (typeof parsed === 'object' && parsed !== null) {
          result[key] = parsed;
          continue;
        }
      } catch {
        // Not JSON, continue to primitive parsing
      }
      // Try to convert to boolean or number
      if (value === 'true') {
        result[key] = true;
      } else if (value === 'false') {
        result[key] = false;
      } else if (!isNaN(Number(value)) && value.trim() !== '') {
        result[key] = Number(value);
      } else {
        result[key] = value;
      }
    } else if (Array.isArray(value)) {
      // Convert each element in the array
      result[key] = value.map((v) => {
        if (typeof v !== 'string') return v;
        // Try to parse as JSON object
        try {
          const parsed = JSON.parse(v) as unknown;
          if (typeof parsed === 'object' && parsed !== null) {
            return parsed;
          }
        } catch {
          // Not JSON, continue to primitive parsing
        }
        if (v === 'true') return true;
        if (v === 'false') return false;
        if (!isNaN(Number(v)) && v.trim() !== '') return Number(v);
        return v;
      });
    } else {
      // undefined or other types, just assign as is
      result[key] = value;
    }
  }

  return result as T;
};
